import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Modal } from "semantic-ui-react";

import { DATE_FORMATS_FNS, ReactTable } from "components";
import { FACTORYEXPORT_RECEIVERSTATUS } from "models";
import "modules/FactoryExportsDashboard/components/ExportLogModal/ExportLogModal.scss";
import { useReceiversQuery } from "modules/FactoryExportsDashboard/components/ExportLogModal/hooks";
import { enumToDropdownOptions } from "utils/common";
import { formatDate } from "utils/dateHelperFunctions";

import type { FactoryExport, FactoryExportReceiver } from "models";

type ExportLogModalProps = {
  factoryExport: FactoryExport | null;
  onClose: () => void;
};

const columnHelper = createColumnHelper<FactoryExportReceiver>();

export const ExportLogModal = ({ factoryExport, onClose }: ExportLogModalProps) => {
  const { t } = useTranslation();

  const receiversStatusOptions = enumToDropdownOptions({ enumObj: FACTORYEXPORT_RECEIVERSTATUS, t });
  const { data: receivers = [], isFetching } = useReceiversQuery({ factory_export_id: factoryExport?.id ?? null });

  const columns = useMemo(
    () => [
      columnHelper.accessor("created_on", {
        id: "created_on",
        size: 70,
        header: () => <span className="no-wrap-text">{t("v8_date", "Date")}</span>,
        cell: cell => formatDate(cell.getValue(), DATE_FORMATS_FNS.dateMonthYear)
      }),
      columnHelper.accessor("status", {
        id: "status",
        size: 70,
        header: () => <span className="no-wrap-text">{t("v8_status", "Status")}</span>,
        cell: cell => receiversStatusOptions.find(({ value }) => value === cell.getValue())?.text ?? ""
      }),
      columnHelper.accessor("email", {
        id: "email",
        size: 100,
        header: () => <span className="no-wrap-text">{t("v8_email", "Email")}</span>,
        cell: cell => cell.getValue()
      }),
      columnHelper.accessor("reason", {
        id: "reason",
        size: 300,
        header: () => <span className="no-wrap-text">{t("v8_reason", "Reason")}</span>,
        cell: cell => cell.getValue()
      })
    ],
    [t]
  );

  return (
    <Modal open={factoryExport !== null} size="large" closeOnDimmerClick={false} onClose={onClose} className="ExportLogModal">
      <Modal.Header className="logs-title">
        {t("v8_log", "Log")}
        <Button className="modal-close-btn" basic onClick={onClose}>
          <Icon className="xmark grey" />
        </Button>
      </Modal.Header>
      <Modal.Content scrolling>
        <ReactTable isFetching={isFetching} columns={columns} data={receivers} />
      </Modal.Content>
    </Modal>
  );
};
